// Collapsible container for logical task groupings.
import { useMemo } from 'react';
import Panel from '@/components/ui/Panel';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { formatHours } from '@/components/ui/formatHours';
import { isSchedulableTask } from '@/features/tasks/utils/scheduling/taskSchedulingUtils';

/**
 * A domain-specific wrapper for the generic Panel component that handles task-specific logic
 * such as duration calculation.
 *
 * @param {Object} props
 * @param {string} props.title - The display title of the panel.
 * @param {import('react').ReactNode} props.children - The content to display inside the panel.
 * @param {import('@/types').Task[]} [props.tasks=[]] - A list of tasks used for duration calculation.
 * @param {boolean} [props.collapsible=true] - Whether the panel header can collapse the content.
 * @param {boolean} [props.showEditIcon] - Whether to show the edit icon.
 * @param {Function} [props.onEditClick] - Callback for edit icon.
 * @param {import('react').ReactNode} [props.headerActions] - Additional header actions.
 */
const TaskGroupPanel = ({ title, children, tasks = [], ...panelProps }) => {
  const context = useTasksContext();

  const tasksMap = useMemo(() => context?.tasksMap || new Map(), [context]);

  const totalDuration = useMemo(() => {
    return tasks
      .filter((task) => !task.completed && isSchedulableTask(task, tasksMap))
      .reduce((total, task) => total + (task.duration || 0), 0);
  }, [tasks, tasksMap]);

  const formattedDuration =
    totalDuration > 0 ? formatHours(totalDuration / 60) : '';
  const formattedTitle = formattedDuration
    ? `${title} (${formattedDuration})`
    : title;
  const stableTestId = `panel-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <Panel
      title={formattedTitle}
      headerActions={panelProps.headerActions}
      testId={stableTestId}
      tasks={tasks}
      {...panelProps}
    >
      {children}
    </Panel>
  );
};

export default TaskGroupPanel;
